import { useState, useEffect } from "react"
import { useParams } from "react-router-dom"
import Sidebar from "../../components/dashboard/Sidebar"
import DashboardHeader from "../../components/dashboard/HeaderDashboard"
import StudyManagementSection from "../../components/investigatorStudy/StudyManagement"
import PatientsManagementSection from "../../components/investigatorStudy/PatientManagement"
import PatientsInvitationSection from "../../components/investigatorStudy/PatientInvitation"
import StudyController from "@/network/controllers/study.controller"
import { useSession } from "@/hooks/useSession"
import State, { STATE, CurrentStudyState } from "@state";
import NavigationService from "@services/navigation.service.js";
import AuthController from "@controllers/auth.controller.js"; 

export default function InvestigatorStudyDetail() {
  const { id } = useParams()
  const [patientsData, setPatientsData] = useState([])
  const [loadingPatients, setLoadingPatients] = useState(true)
  const {user } = useSession()
  const [study, setStudy] = useState(null)
  const [errorStudy, setErrorStudy] = useState(null)
  const [loading, setLoading] = useState(true)
  const [errorPatients, setErrorPatients] = useState(null)
  const [activeTab, setActiveTab] = useState("Study management")
  const [error, setError] = useState(null)

  

  useEffect(() => {
    const fetchStudyDetails = async () => {
      if (!id) {
        setError("Study ID not available in URL.")
        setLoading(false)
        return
      }

      const currentUserId = user?.userId
      if (!currentUserId) {
        setError("Investigator user ID not available. Please log in again.")
        setLoading(false)
        return
      }
      console.log("the current user id is:" , currentUserId)

      setLoading(true)
      setError(null)
      try {
        const response = await StudyController.getInvestigatorStudyOverview({
          studyId: id,
          investigatorUserId: currentUserId,
        })
        console.log("API response for study details:", response)
        if (response.success && response.data) {
          const apiData = response.data.data
          const generalInfo = apiData.general_info || {}
          const mappedStudyData = {
            ...generalInfo,
            id: id, 
            title: generalInfo.study_name,
            createdBy: generalInfo.created_by,
            createdAt: generalInfo.creation_date,
            updatedAt: generalInfo.last_modified,
            lastTraceability: generalInfo.last_traceability,
            siteICFDate: generalInfo.site_icf_version_date,
            masterICF: generalInfo.master_icf_version_name
              ? [{ name: generalInfo.master_icf_version_name, version: generalInfo.master_icf_version_number }]
              : [],
            milestoneData: (apiData.milestone_progression || []).map((mp) => ({
              number: mp.milestone_index,
              count: Number.parseInt(mp.number_of_patients, 10) || 0,
              title: mp.title,
              mandatory: mp.mandatory,
            })),
            milestones: (apiData.milestones_list || []).map((ml) => ({
              milestone_index: ml.milestone_number,
              title: ml.milestone_name,
              short_description: ml.milestone_description,
              mandatory: ml.mandatory,
            })),
            description: generalInfo.description || "",
            status: generalInfo.status || "",
          }

          // Fallback: fetch studies list to derive global status when missing
          if (!mappedStudyData.status || String(mappedStudyData.status).trim() === "") {
            try {
              const listRes = await StudyController.getInvestigatorStudies?.()
              if (listRes?.success && Array.isArray(listRes?.data?.data)) {
                const found = listRes.data.data.find((s) => s.id === id)
                if (found?.study_global_status) {
                  mappedStudyData.status = found.study_global_status
                  mappedStudyData.study_global_status = found.study_global_status
                }
              }
            } catch (e) {
              console.warn("Unable to derive study_global_status from list", e)
            }
          }

          setStudy(mappedStudyData)
          State.setValue(CurrentStudyState.STUDY, mappedStudyData)
          console.log("Mapped study data set in state (InvestigatorStudyDetail):", mappedStudyData)
          console.log("STATE[CurrentStudyState.STUDY] after update:", STATE[CurrentStudyState.STUDY]) 
        } else {
          setError(response.message || "Unable to retrieve study details.")
          setStudy(null)
          State.setValue(CurrentStudyState.STUDY, null) 
        }
      } catch (err) {
        console.error("Network error or unexpected while retrieving study details:", err)
        setError("An unexpected error occurred while loading study details.")
        setStudy(null)
        State.setValue(CurrentStudyState.STUDY, null)
      } finally {
        setLoading(false)
      }
    }
    fetchStudyDetails()
  }, [id]) // Dipendenza dall'ID della URL

  const handleSignOut = () => {
    AuthController.logout();
  }

  



  if (loading) {
    return (
      <div className="flex h-screen bg-dashboard-bg">
        <Sidebar
          title="OSHIMED"
          activeLink="Dashboard"
          companyName="Investigator Name"
          userRole="investigator"
          onSignOut={handleSignOut}
        />
        <div className="flex-1 flex flex-col overflow-hidden p-4">
          <DashboardHeader title="Study Details" buttons={[]} />
          <div className="flex-1 mt-2 rounded-xl overflow-auto bg-[#F5F8FF] flex items-center justify-center">
            <div className="text-xl text-gray-600">Loading study details...</div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !study) {
    return (
      <div className=" flex h-screen bg-dashboard-bg">
        <Sidebar
          title="OSHIMED"
          activeLink="Dashboard"
          companyName="Investigator Name"
          userRole="investigator"
          onSignOut={handleSignOut}
        />
        <div className="flex-1 flex flex-col overflow-hidden p-4">
          <DashboardHeader title="Study Details" buttons={[]} />
          <div className="flex-1 mt-2 rounded-xl overflow-auto flex items-center justify-center">
            <div className="text-xl text-red-600">{error || "Study not found."}</div>
          </div>
        </div>
      </div>
    )
  }

  const tabButtons = ["Study management", "Patients management", "Patients invitation"]

  const derivedStatus =
    study?.status ?? study?.study_global_status ?? STATE?.[CurrentStudyState.STUDY]?.study_global_status ?? "";
  const isClosed = String(derivedStatus).toLowerCase() === "closed";

  return (
    <div className="flex h-screen bg-dashboard-bg">
      <Sidebar
        title="OSHIMED"
        activeLink="Dashboard"
        companyName="Investigator Name"
        userRole="investigator"
        onSignOut={handleSignOut}
      />
      <div className="flex-1 flex flex-col overflow-hidden p-4">
        <DashboardHeader
          title={study.title || "Study Details"}
          buttons={tabButtons}
          activeButton={activeTab}
          onButtonClick={(tab) => {
            setActiveTab(tab)
          }}
        />
        <div className="flex-1 overflow-auto bg-dashboard-bg">
          <main className="p-6">
            {isClosed && (
              <div className="mb-4 rounded-md border border-yellow-300 bg-yellow-50 p-3 text-yellow-800">
                This study is closed. The interface is read-only and actions are disabled.
              </div>
            )}
            <div aria-disabled={isClosed}>
              {activeTab === "Study management" && (
                <StudyManagementSection study={study} isClosed={isClosed} />
              )}
              {activeTab === "Patients management" && (
                <PatientsManagementSection studyId={id} isClosed={isClosed} />
              )}
              {activeTab === "Patients invitation" && (
                <PatientsInvitationSection isClosed={isClosed} />
              )}
            </div>
          </main>
        </div>
      </div>
    </div>
  )
}
